/**
 * The shoot, as a strip.
 *
 * Thumbnails are generated here rather than by the decoder because they must
 * agree with the viewport: the same highlight shoulder and the same sRGB encode
 * the base shader applies. A strip that renders raw linear data looks washed
 * out next to the canvas and quietly teaches the photographer to distrust it.
 */

import { useEffect, useReducer, useRef } from 'react';
import type { DecodedImage } from '@lumos/core';
import { useStore, type Photo } from '../state/store.ts';

/** Survives eviction of the decoded pixels, which is the whole point of caching here. */
const thumbnails = new Map<string, string>();
const unthumbnailable = new Set<string>();

/** Matches `shoulder()` in the base fragment shader. */
const KNEE = 0.72;

function shoulder(value: number): number {
  if (value <= KNEE) return value;
  const headroom = 1 - KNEE;
  return KNEE + headroom * (1 - Math.exp(-(value - KNEE) / headroom));
}

function encodeChannel(linear: number): number {
  const rolled = shoulder(Math.max(0, linear));
  const encoded = rolled <= 0.0031308 ? rolled * 12.92 : 1.055 * rolled ** (1 / 2.4) - 0.055;
  return encoded * 255;
}

function renderThumbnail(image: DecodedImage, maxEdge = 288): string {
  const scale = Math.min(1, maxEdge / Math.max(image.width, image.height));
  const width = Math.max(1, Math.round(image.width * scale));
  const height = Math.max(1, Math.round(image.height * scale));
  const out = new Uint8ClampedArray(width * height * 4);

  const xScale = image.width / width;
  const yScale = image.height / height;

  for (let y = 0; y < height; y += 1) {
    const y0 = Math.floor(y * yScale);
    const y1 = Math.min(image.height, Math.max(y0 + 1, Math.ceil((y + 1) * yScale)));
    for (let x = 0; x < width; x += 1) {
      const x0 = Math.floor(x * xScale);
      const x1 = Math.min(image.width, Math.max(x0 + 1, Math.ceil((x + 1) * xScale)));

      let r = 0;
      let g = 0;
      let b = 0;
      let samples = 0;
      for (let sy = y0; sy < y1; sy += 1) {
        const row = sy * image.width;
        for (let sx = x0; sx < x1; sx += 1) {
          const index = (row + sx) * 3;
          r += image.data[index];
          g += image.data[index + 1];
          b += image.data[index + 2];
          samples += 1;
        }
      }
      const inverse = samples > 0 ? 1 / samples : 0;
      const target = (y * width + x) * 4;
      out[target] = encodeChannel(r * inverse);
      out[target + 1] = encodeChannel(g * inverse);
      out[target + 2] = encodeChannel(b * inverse);
      out[target + 3] = 255;
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) throw new Error('The browser refused a 2D canvas for the filmstrip.');
  context.putImageData(new ImageData(out, width, height), 0, 0);
  return canvas.toDataURL('image/jpeg', 0.72);
}

function stateLabel(photo: Photo): string | null {
  if (photo.status === 'error') return 'Could not read';
  if (photo.status === 'decoding') return photo.progress || 'Decoding';
  if (photo.status === 'queued') return 'Waiting';
  return null;
}

export function Filmstrip() {
  const photos = useStore((state) => state.photos);
  const selectedId = useStore((state) => state.selectedId);
  const selection = useStore((state) => state.selection);
  const select = useStore((state) => state.select);
  const [, bump] = useReducer((count: number) => count + 1, 0);
  const currentRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      for (const photo of photos) {
        if (cancelled) return;
        if (!photo.image || thumbnails.has(photo.id) || unthumbnailable.has(photo.id)) continue;
        try {
          thumbnails.set(photo.id, renderThumbnail(photo.image));
        } catch {
          unthumbnailable.add(photo.id);
        }
        bump();
        // One frame between thumbnails: a 40-photo import must not freeze the app.
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [photos]);

  useEffect(() => {
    currentRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [selectedId]);

  const move = (from: number, direction: -1 | 1) => {
    const next = photos[from + direction];
    if (!next) return;
    select(next.id);
    // Focus follows selection so a second arrow press continues from here.
    window.requestAnimationFrame(() => currentRef.current?.focus());
  };

  return (
    <div className="filmstrip" role="listbox" aria-label="Photographs in this session">
      {photos.map((photo, index) => {
        const current = photo.id === selectedId;
        const inSelection = selection.includes(photo.id);
        const thumbnail = thumbnails.get(photo.id);
        const state = stateLabel(photo);

        return (
          <button
            key={photo.id}
            ref={current ? currentRef : null}
            type="button"
            role="option"
            aria-selected={current}
            className={`thumb${current ? ' current' : ''}${inSelection && !current ? ' in-selection' : ''}`}
            aria-label={`${photo.filename}${photo.edited ? ', edited' : ''}${state ? `, ${state}` : ''}`}
            title={photo.error ?? photo.filename}
            onClick={(event) =>
              select(photo.id, event.metaKey || event.ctrlKey, event.shiftKey)
            }
            onKeyDown={(event) => {
              if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
              event.preventDefault();
              move(index, event.key === 'ArrowLeft' ? -1 : 1);
            }}
          >
            {thumbnail ? <img src={thumbnail} alt="" /> : null}
            {!thumbnail && state ? <span className="thumb-state">{state}</span> : null}
            {photo.edited ? <span className="thumb-mark" title="Edited" /> : null}
            <span className="thumb-name">{photo.filename}</span>
          </button>
        );
      })}
    </div>
  );
}
